import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import { Button, Modal, Form } from "react-bootstrap";
import Select from 'react-select';
import { toast } from "react-toastify";
import api from "../../services/api";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllRoles } from "../../redux/slice/getRolesPermissionListSlice";
import { fetchAllPermissions } from "../../redux/slice/getAllPermissionListSlice";
import CrossIcon from "../../assets/img/file-cut-icon.png";
import axiosPrivate from "../../services/axios/axiosPrivate";
import { useForm } from "react-hook-form";

function CreateRoleModal({ show, onHide, canEdit }) {
  const dispatch = useDispatch();
  const { permissions, status } = useSelector((state) => state.Permissions);
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    mode: "onChange",
    defaultValues: {
      roleName: "",
      description: ""
    }
  });

  useEffect(() => {
    if (show) {
      dispatch(fetchAllPermissions());
    }
  }, [show, dispatch]);

  const permissionOptions = permissions.map(permission => ({
    value: permission.id,
    label: permission.name,
    description: permission.description
  }));

  const selectAllOption = {
    value: "all",
    label: "Select All"
  };

  const allPermissionOptions = [
    selectAllOption,
    ...permissionOptions
  ];

  const handleCreateRole = async (data) => {
    if (!canEdit) {
      toast.error("You do not have permission to create role.", { className: 'toast-error-custom', autoClose: 5000 });
      return;
    }

    try {
      const payload = {
        name: data.roleName,
        description: data.description,
        permissionIds: selectedPermissions.map(p => p.value)
      };

      const response = await axiosPrivate.post(api.roles.create, payload);

      if (response?.data?.success === true) {
        toast.success(response?.data?.reason || "Role created successfully!", { className: 'toast-success-custom' });
        await dispatch(fetchAllRoles()).unwrap();
        reset();
        setSelectedPermissions([]);
        onHide();
      } else {
        toast.error(response?.data?.reason || "Failed to create role", { className: 'toast-error-custom', autoClose: 8000 });
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.message || "Unable to connect to server", { className: 'toast-error-custom', autoClose: 4000 }
      );
    }
  };

  const onCreate = (data) => {
    handleCreateRole(data);
  };

  const handlePermissionChange = (selected) => {

    if (!selected || selected.length === 0) {
      setSelectedPermissions([]);
      return;
    }

    const isSelectAll = selected.some(
      item => item.value === "all"
    );

    if (isSelectAll) {

      // Agar sab selected hain to deselect
      if (
        selectedPermissions.length === permissionOptions.length
      ) {
        setSelectedPermissions([]);
      }

      // Warna sab select
      else {
        setSelectedPermissions(permissionOptions);
      }

      return;
    }

    setSelectedPermissions(selected);
  };

  const handleClose = () => {
    reset();
    setSelectedPermissions([]);
    onHide();
  };

  const customStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: '38px',
      borderColor: '#ced4da',
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#e7f3ff',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#0066cc',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#0066cc',
      ':hover': {
        backgroundColor: '#0066cc',
        color: 'white',
      },
    }),
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header>
        <Modal.Title>Create New Role</Modal.Title>
        <button onClick={handleClose} className="modal-close">
          <img src={CrossIcon} alt="Close" style={{ width: "20px", height: "20px" }} />
        </button>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit(onCreate)}>
          <Form.Group>
            <Form.Label>Role Name <span style={{ color: 'red' }}>*</span></Form.Label>
            <Form.Control
              type="text"
              {...register("roleName", {
                required: "Role name is required",
                minLength: { value: 3, message: "Name must be at least 3 characters" },
                maxLength: { value: 50, message: "Name cannot exceed 50 characters" },
                pattern: {
                  value: /^[a-zA-Z\s]+$/,
                  message: "Numbers and special characters are not allowed"
                }
              })}
              placeholder="Enter role name"
            />
            {errors.roleName && (
              <p className="error-msg">
                {errors.roleName.message}
              </p>
            )}
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>Description <span style={{ color: 'red' }}>*</span></Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              {...register("description", {
                required: "Description is required",
                maxLength: { value: 200, message: "Keep description under 200 characters" },
                pattern: {
                  value: /^[a-zA-Z\s]+$/,
                  message: "Numbers and special characters are not allowed"
                }
              })}
              placeholder="Enter role description"
            />
            {errors.description && (
              <p className="error-msg">
                {errors.description.message}
              </p>
            )}
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>Default Permissions (Optional)</Form.Label>
            <Select
              isMulti

              options={allPermissionOptions}

              value={selectedPermissions}

              onChange={handlePermissionChange}
              placeholder="Search and select permissions..."
              styles={customStyles}
              isLoading={status === 'loading'}
              isDisabled={status === 'loading'}
              className="basic-multi-select"
              classNamePrefix="select"
              formatOptionLabel={(option) => (

                <div>

                  <div
                    style={{
                      fontWeight: '500'
                    }}
                  >
                    {option.label}
                  </div>


                  {
                    option.description &&
                    option.value !== "all" && (

                      <div
                        style={{
                          fontSize: '12px',
                          color: '#666'
                        }}
                      >
                        {option.description}
                      </div>

                    )
                  }

                </div>

              )}
            />
            <Form.Text className="text-muted">
              Select default permissions for this role. Users with this role will inherit these permissions.
            </Form.Text>
            {selectedPermissions.length > 0 && (
              <div className="mt-2">
                <small className="text-info">
                  {selectedPermissions.length} permission(s) selected
                </small>
              </div>
            )}
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="cancel-cta" onClick={handleClose}>Cancel</Button>
        <Button variant="update-cta" onClick={handleSubmit(onCreate)} disabled={!canEdit}>
          Create Role
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

CreateRoleModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  canEdit: PropTypes.bool
};

export default CreateRoleModal;
